import dayjs, { Dayjs } from 'dayjs'

import { ContentStrategyTypeLabel } from '../../../constants'
import {
  BrandPlanTactic,
  BrandPlanContentStrategies,
  ContentStrategy,
  ContentStrategyType,
  ContentStrategyDefaults,
  Goal,
  BusinessType,
  StrategyGrowth,
} from 'types'

/**
 * Converts an array of strategies to the server's content_strategies format:
 * { [strategy_id]: { seo_contents, email_contents, instagram_contents, crosspost_contents, video_crosspost_contents, length, tactics } }
 */
export const strategiesToContentStrategies = (strategies: Strategy[]): BrandPlanContentStrategies => {
  return strategies.reduce<BrandPlanContentStrategies>((acc, strategy) => {
    acc[strategy.id as ContentStrategyType] = {
      seo_contents: strategy.seo_contents,
      email_contents: strategy.email_contents,
      instagram_contents: strategy.instagram_contents,
      crosspost_contents: strategy.crosspost_contents,
      video_crosspost_contents: strategy.video_crosspost_contents,
      length: strategy.length,
      tactics: strategy.tactics,
    }
    return acc
  }, {})
}

export const buildContentStrategyTactics = (strategy: Strategy): BrandPlanTactic[] => {
  return strategy.tactics.filter((tactic) => {
    return (
      (strategy.crosspost_contents.some((c) => c.count > 0) ? true : !SOCIAL_TACTICS.includes(tactic)) &&
      (strategy.video_crosspost_contents.some((c) => c.count > 0) ? true : !VIDEO_TACTICS.includes(tactic)) &&
      (strategy.seo_contents.some((c) => c.count > 0) ? true : ![BrandPlanTactic.SEO].includes(tactic)) &&
      (strategy.email_contents.some((c) => c.count > 0) ? true : ![BrandPlanTactic.Email].includes(tactic))
    )
  })
}

type RequireFields<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>

export interface Strategy
  extends RequireFields<
    ContentStrategy,
    | 'instagram_contents'
    | 'crosspost_contents'
    | 'email_contents'
    | 'video_crosspost_contents'
    | 'seo_contents'
    | 'tactics'
  > {
  id: ContentStrategyType
  name: string
  description: string
  selected_goals: Goal[]
  isSelected?: boolean
}

const STRATEGY_GROWTH_INTERVAL_WEEKS: Record<StrategyGrowth, number> = {
  manual: 0,
  aggressive: 2,
  moderate: 3,
  conservative: 4,
}

export const buildRemainingContentStrategies = (
  strategies: Strategy[],
  strategyGrowth: StrategyGrowth,
  baseStartOverride?: Dayjs,
): BrandPlanContentStrategies => {
  if (strategyGrowth === 'manual') return {}

  const intervalWeeks = STRATEGY_GROWTH_INTERVAL_WEEKS[strategyGrowth]
  const baseStartDate = baseStartOverride ?? dayjs().add(1, 'day')

  const unselectedStrategies = strategies.filter((s) => !s.isSelected)

  return unselectedStrategies.reduce<BrandPlanContentStrategies>((acc, strategy, index) => {
    const startDate = baseStartDate.add((index + 1) * intervalWeeks, 'week').format('YYYY-MM-DD')
    acc[strategy.id] = {
      seo_contents: strategy.seo_contents,
      email_contents: strategy.email_contents,
      instagram_contents: strategy.instagram_contents,
      crosspost_contents: strategy.crosspost_contents,
      video_crosspost_contents: strategy.video_crosspost_contents,
      length: strategy.length,
      tactics: buildContentStrategyTactics(strategy),
      start_date: startDate,
    }
    return acc
  }, {})
}

export const SOCIAL_TACTICS = [
  BrandPlanTactic.Instagram,
  BrandPlanTactic.Facebook,
  BrandPlanTactic.LinkedIn,
  BrandPlanTactic.Twitter,
]
export const VIDEO_TACTICS = [BrandPlanTactic.TikTok, BrandPlanTactic.YouTube, BrandPlanTactic.InstagramReel]

// Source: https://docs.google.com/spreadsheets/d/1G2-_jRocqV49xeIuigcvT93QdVoFL3pBTtl68RfODdA/edit?gid=1230899626#gid=1230899626
export const strategiesDefaultData: Record<BusinessType, { id: string; name: string; strategies: Strategy[] }> = {
  service: {
    id: 'service',
    name: 'Service-Based Businesses',
    strategies: [
      {
        id: ContentStrategyType.ThoughtLeadership,
        name: ContentStrategyTypeLabel[ContentStrategyType.ThoughtLeadership],
        description:
          'Share original perspectives, industry insights, and expert takes on trends shaping your field. Best for building credibility and attracting high-intent prospects.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 0 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['grow_audience'],
        length: 1,
      },
      {
        id: ContentStrategyType.OfferAndServices,
        name: ContentStrategyTypeLabel[ContentStrategyType.OfferAndServices],
        description:
          'Highlight opportunities for clients to buy from you: sales, discounts, bundles, or limited-time deals. Best for creating urgency and rewarding customers.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 0 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['convert_clients'],
        length: 1,
      },
      {
        id: ContentStrategyType.EducationalContent,
        name: ContentStrategyTypeLabel[ContentStrategyType.EducationalContent],
        description:
          'Teach something useful about the broader category or problem space, not just your products. Best for reaching new audiences through value.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 0 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['generate_leads'],
        length: 1,
      },
      {
        id: ContentStrategyType.QuickTips,
        name: ContentStrategyTypeLabel[ContentStrategyType.QuickTips],
        description:
          'Offer focused, immediately applicable advice your audience can act on today. Best for demonstrating expertise and earning consistent engagement.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 0 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['drive_repeat_business'],
        length: 1,
      },
    ],
  },
  product: {
    id: 'product',
    name: 'Product-Based Businesses',
    strategies: [
      {
        id: ContentStrategyType.LifestyleContent,
        name: ContentStrategyTypeLabel[ContentStrategyType.LifestyleContent],
        description:
          "Show your products in the context of your customer's aspirations and daily life. Best for helping customers picture themselves using the product.",
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 0 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['build_product_awareness'],
        length: 1,
      },
      {
        id: ContentStrategyType.OfferAndPromotion,
        name: ContentStrategyTypeLabel[ContentStrategyType.OfferAndPromotion],
        description:
          'Highlight opportunities for customers to buy from you: sales, discounts, bundles, or limited-time deals. Best for creating urgency and rewarding customers.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 0 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['make_more_sales'],
        length: 1,
      },
      {
        id: ContentStrategyType.ProductEducation,
        name: ContentStrategyTypeLabel[ContentStrategyType.ProductEducation],
        description:
          'Break down what makes your product/service great: the features, materials, design choices, and craft behind it. Best for turning curious browsers into informed buyers.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 0 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['create_repeat_customers'],
        length: 1,
      },
      {
        id: ContentStrategyType.ProblemSolution,
        name: ContentStrategyTypeLabel[ContentStrategyType.ProblemSolution],
        description:
          'Show the specific problem your product solves in relatable, concrete terms. Best for moving shoppers from curiosity to consideration.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 0 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['grow_audience'],
        length: 1,
      },
    ],
  },
  place: {
    id: 'place',
    name: 'Place-Based Businesses',
    strategies: [
      {
        id: ContentStrategyType.LifestyleContent,
        name: ContentStrategyTypeLabel[ContentStrategyType.LifestyleContent],
        description:
          "Show your products in the context of your customer's aspirations and daily life. Best for helping customers picture themselves using the product.",
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 0 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['grow_through_word_of_mouth'],
        length: 1,
      },
      {
        id: ContentStrategyType.OfferAndPromotion,
        name: ContentStrategyTypeLabel[ContentStrategyType.OfferAndPromotion],
        description:
          'Highlight opportunities for customers to buy from you: sales, discounts, bundles, or limited-time deals. Best for creating urgency and rewarding customers.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 0 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['bring_in_new_customers'],
        length: 1,
      },
      {
        id: ContentStrategyType.ProductEducation,
        name: ContentStrategyTypeLabel[ContentStrategyType.ProductEducation],
        description:
          'Break down what makes your product/service great: the features, materials, design choices, and craft behind it. Best for turning curious browsers into informed buyers.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 0 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['keep_customers_coming_back'],
        length: 1,
      },
      {
        id: ContentStrategyType.ProblemSolution,
        name: ContentStrategyTypeLabel[ContentStrategyType.ProblemSolution],
        description:
          'Show the specific problem you solve for customers in relatable, concrete terms. Best for moving people from awareness to action.',
        crosspost_contents: [{ type: 'post', count: 3 }],
        video_crosspost_contents: [{ type: 'post', count: 0 }],
        email_contents: [{ type: 'newsletter', count: 1 }],
        seo_contents: [{ type: 'blog_post', count: 1 }],
        instagram_contents: [],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['get_found_locally'],
        length: 1,
      },
    ],
  },
}

export const strategiesDefaultDataV2: Record<BusinessType, { id: string; name: string; strategies: Strategy[] }> = {
  service: {
    id: 'service',
    name: 'Service-Based Businesses',
    strategies: [
      {
        id: ContentStrategyType.ThoughtLeadership,
        name: ContentStrategyTypeLabel[ContentStrategyType.ThoughtLeadership],
        description:
          'Share original perspectives, industry insights, and expert takes on trends shaping your field. Best for building credibility and attracting high-intent prospects.',
        crosspost_contents: [
          { type: 'post', count: 1, content_type: 'still_image' },
          { type: 'post', count: 1, content_type: 'carousel' },
          { type: 'post', count: 1, content_type: 'video' },
        ],
        video_crosspost_contents: [{ type: 'post', count: 0, content_type: null }],
        email_contents: [{ type: 'newsletter', count: 1, content_type: null }],
        seo_contents: [{ type: 'blog_post', count: 1, content_type: null }],
        instagram_contents: [{ type: 'story', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['grow_audience'],
        length: 1,
      },
      {
        id: ContentStrategyType.OfferAndServices,
        name: ContentStrategyTypeLabel[ContentStrategyType.OfferAndServices],
        description:
          'Highlight opportunities for clients to buy from you: sales, discounts, bundles, or limited-time deals. Best for creating urgency and rewarding customers.',
        crosspost_contents: [
          { type: 'post', count: 1, content_type: 'still_image' },
          { type: 'post', count: 1, content_type: 'carousel' },
          { type: 'post', count: 1, content_type: 'video' },
        ],
        video_crosspost_contents: [{ type: 'post', count: 0, content_type: null }],
        email_contents: [{ type: 'newsletter', count: 1, content_type: null }],
        seo_contents: [{ type: 'blog_post', count: 1, content_type: null }],
        instagram_contents: [{ type: 'story', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['convert_clients'],
        length: 1,
      },
      {
        id: ContentStrategyType.EducationalContent,
        name: ContentStrategyTypeLabel[ContentStrategyType.EducationalContent],
        description:
          'Teach something useful about the broader category or problem space, not just your products. Best for reaching new audiences through value.',
        crosspost_contents: [
          { type: 'post', count: 1, content_type: 'still_image' },
          { type: 'post', count: 1, content_type: 'carousel' },
          { type: 'post', count: 1, content_type: 'video' },
        ],
        video_crosspost_contents: [{ type: 'post', count: 0, content_type: null }],
        email_contents: [{ type: 'newsletter', count: 1, content_type: null }],
        seo_contents: [{ type: 'blog_post', count: 1, content_type: null }],
        instagram_contents: [{ type: 'story', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['generate_leads'],
        length: 1,
      },
      {
        id: ContentStrategyType.QuickTips,
        name: ContentStrategyTypeLabel[ContentStrategyType.QuickTips],
        description:
          'Offer focused, immediately applicable advice your audience can act on today. Best for demonstrating expertise and earning consistent engagement.',
        crosspost_contents: [
          { type: 'post', count: 1, content_type: 'still_image' },
          { type: 'post', count: 1, content_type: 'carousel' },
          { type: 'post', count: 1, content_type: 'video' },
        ],
        video_crosspost_contents: [{ type: 'post', count: 0, content_type: null }],
        email_contents: [{ type: 'newsletter', count: 1, content_type: null }],
        seo_contents: [{ type: 'blog_post', count: 1, content_type: null }],
        instagram_contents: [{ type: 'story', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['drive_repeat_business'],
        length: 1,
      },
    ],
  },
  product: {
    id: 'product',
    name: 'Product-Based Businesses',
    strategies: [
      {
        id: ContentStrategyType.LifestyleContent,
        name: ContentStrategyTypeLabel[ContentStrategyType.LifestyleContent],
        description:
          "Show your products in the context of your customer's aspirations and daily life. Best for helping customers picture themselves using the product.",
        crosspost_contents: [
          { type: 'post', count: 1, content_type: 'still_image' },
          { type: 'post', count: 1, content_type: 'carousel' },
          { type: 'post', count: 1, content_type: 'video' },
        ],
        video_crosspost_contents: [{ type: 'post', count: 0, content_type: null }],
        email_contents: [{ type: 'newsletter', count: 1, content_type: null }],
        seo_contents: [{ type: 'blog_post', count: 1, content_type: null }],
        instagram_contents: [{ type: 'story', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['build_product_awareness'],
        length: 1,
      },
      {
        id: ContentStrategyType.OfferAndPromotion,
        name: ContentStrategyTypeLabel[ContentStrategyType.OfferAndPromotion],
        description:
          'Highlight opportunities for customers to buy from you: sales, discounts, bundles, or limited-time deals. Best for creating urgency and rewarding customers.',
        crosspost_contents: [
          { type: 'post', count: 1, content_type: 'still_image' },
          { type: 'post', count: 1, content_type: 'carousel' },
          { type: 'post', count: 1, content_type: 'video' },
        ],
        video_crosspost_contents: [{ type: 'post', count: 0, content_type: null }],
        email_contents: [{ type: 'newsletter', count: 1, content_type: null }],
        seo_contents: [{ type: 'blog_post', count: 1, content_type: null }],
        instagram_contents: [{ type: 'story', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['make_more_sales'],
        length: 1,
      },
      {
        id: ContentStrategyType.ProductEducation,
        name: ContentStrategyTypeLabel[ContentStrategyType.ProductEducation],
        description:
          'Break down what makes your product/service great: the features, materials, design choices, and craft behind it. Best for turning curious browsers into informed buyers.',
        crosspost_contents: [
          { type: 'post', count: 1, content_type: 'still_image' },
          { type: 'post', count: 1, content_type: 'carousel' },
          { type: 'post', count: 1, content_type: 'video' },
        ],
        video_crosspost_contents: [{ type: 'post', count: 0, content_type: null }],
        email_contents: [{ type: 'newsletter', count: 1, content_type: null }],
        seo_contents: [{ type: 'blog_post', count: 1, content_type: null }],
        instagram_contents: [{ type: 'story', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['create_repeat_customers'],
        length: 1,
      },
      {
        id: ContentStrategyType.ProblemSolution,
        name: ContentStrategyTypeLabel[ContentStrategyType.ProblemSolution],
        description:
          'Show the specific problem your product solves in relatable, concrete terms. Best for moving shoppers from curiosity to consideration.',
        crosspost_contents: [
          { type: 'post', count: 1, content_type: 'still_image' },
          { type: 'post', count: 1, content_type: 'carousel' },
          { type: 'post', count: 1, content_type: 'video' },
        ],
        video_crosspost_contents: [{ type: 'post', count: 0, content_type: null }],
        email_contents: [{ type: 'newsletter', count: 1, content_type: null }],
        seo_contents: [{ type: 'blog_post', count: 1, content_type: null }],
        instagram_contents: [{ type: 'story', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['grow_audience'],
        length: 1,
      },
    ],
  },
  place: {
    id: 'place',
    name: 'Place-Based Businesses',
    strategies: [
      {
        id: ContentStrategyType.LifestyleContent,
        name: ContentStrategyTypeLabel[ContentStrategyType.LifestyleContent],
        description:
          "Show your products in the context of your customer's aspirations and daily life. Best for helping customers picture themselves using the product.",
        crosspost_contents: [
          { type: 'post', count: 1, content_type: 'still_image' },
          { type: 'post', count: 1, content_type: 'carousel' },
          { type: 'post', count: 1, content_type: 'video' },
        ],
        video_crosspost_contents: [{ type: 'post', count: 0, content_type: null }],
        email_contents: [{ type: 'newsletter', count: 1, content_type: null }],
        seo_contents: [{ type: 'blog_post', count: 1, content_type: null }],
        instagram_contents: [{ type: 'story', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['grow_through_word_of_mouth'],
        length: 1,
      },
      {
        id: ContentStrategyType.OfferAndPromotion,
        name: ContentStrategyTypeLabel[ContentStrategyType.OfferAndPromotion],
        description:
          'Highlight opportunities for customers to buy from you: sales, discounts, bundles, or limited-time deals. Best for creating urgency and rewarding customers.',
        crosspost_contents: [
          { type: 'post', count: 1, content_type: 'still_image' },
          { type: 'post', count: 1, content_type: 'carousel' },
          { type: 'post', count: 1, content_type: 'video' },
        ],
        video_crosspost_contents: [{ type: 'post', count: 0, content_type: null }],
        email_contents: [{ type: 'newsletter', count: 1, content_type: null }],
        seo_contents: [{ type: 'blog_post', count: 1, content_type: null }],
        instagram_contents: [{ type: 'story', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['bring_in_new_customers'],
        length: 1,
      },
      {
        id: ContentStrategyType.ProductEducation,
        name: ContentStrategyTypeLabel[ContentStrategyType.ProductEducation],
        description:
          'Break down what makes your product/service great: the features, materials, design choices, and craft behind it. Best for turning curious browsers into informed buyers.',
        crosspost_contents: [
          { type: 'post', count: 1, content_type: 'still_image' },
          { type: 'post', count: 1, content_type: 'carousel' },
          { type: 'post', count: 1, content_type: 'video' },
        ],
        video_crosspost_contents: [{ type: 'post', count: 0, content_type: null }],
        email_contents: [{ type: 'newsletter', count: 1, content_type: null }],
        seo_contents: [{ type: 'blog_post', count: 1, content_type: null }],
        instagram_contents: [{ type: 'story', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['keep_customers_coming_back'],
        length: 1,
      },
      {
        id: ContentStrategyType.ProblemSolution,
        name: ContentStrategyTypeLabel[ContentStrategyType.ProblemSolution],
        description:
          'Show the specific problem you solve for customers in relatable, concrete terms. Best for moving people from awareness to action.',
        crosspost_contents: [
          { type: 'post', count: 1, content_type: 'still_image' },
          { type: 'post', count: 1, content_type: 'carousel' },
          { type: 'post', count: 1, content_type: 'video' },
        ],
        video_crosspost_contents: [{ type: 'post', count: 0, content_type: null }],
        email_contents: [{ type: 'newsletter', count: 1, content_type: null }],
        seo_contents: [{ type: 'blog_post', count: 1, content_type: null }],
        instagram_contents: [{ type: 'story', count: 0 }],
        tactics: [
          BrandPlanTactic.Instagram,
          BrandPlanTactic.Facebook,
          BrandPlanTactic.LinkedIn,
          BrandPlanTactic.Twitter,
          BrandPlanTactic.TikTok,
          BrandPlanTactic.YouTube,
          BrandPlanTactic.InstagramReel,
          BrandPlanTactic.SEO,
          BrandPlanTactic.Email,
        ],
        selected_goals: ['get_found_locally'],
        length: 1,
      },
    ],
  },
}

/**
 * Merges API-provided content count overrides into the base strategiesDefaultData.
 * Only overrides crosspost_contents, video_crosspost_contents, email_contents, seo_contents counts.
 */
export const mergeStrategyDefaults = (
  baseData: typeof strategiesDefaultData,
  overrides: ContentStrategyDefaults | undefined,
): typeof strategiesDefaultData => {
  if (!overrides) return baseData

  const result = { ...baseData }
  for (const businessType of Object.keys(result) as BusinessType[]) {
    const businessOverrides = overrides[businessType]
    if (!businessOverrides) continue

    result[businessType] = {
      ...result[businessType],
      strategies: result[businessType].strategies.map((strategy) => {
        const strategyOverride = businessOverrides[strategy.id]
        if (!strategyOverride) return strategy

        return {
          ...strategy,
          crosspost_contents: strategyOverride.crosspost_contents ?? strategy.crosspost_contents,
          video_crosspost_contents: strategyOverride.video_crosspost_contents ?? strategy.video_crosspost_contents,
          email_contents: strategyOverride.email_contents ?? strategy.email_contents,
          seo_contents: strategyOverride.seo_contents ?? strategy.seo_contents,
          instagram_contents: strategyOverride.instagram_contents ?? strategy.instagram_contents,
        }
      }),
    }
  }
  return result
}

/**
 * Experiment: onboarding_zero_social_video.
 * For the 'test' variant, sets count to 0 on post videos
 * For 'control' or null, returns input unchanged.
 */
export const applyVideoZeroVariant = (strategies: Strategy[], variant: 'off' | 'on' | null): Strategy[] => {
  if (variant !== 'on') return strategies

  return strategies.map((s) => ({
    ...s,
    crosspost_contents: s.crosspost_contents.map((c) =>
      c.content_type === 'video' && (c.count ?? 0) >= 1 ? { ...c, count: 0 } : c,
    ),
  }))
}

export const numberWithCommas = (number: number) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const whyWeRecommendIt = {
  [ContentStrategyType.EducationalContent]: 'Helps you reach new audiences by sharing valuable ideas.',
  [ContentStrategyType.QuickTips]: 'Demonstrates your expertise and earns consistent engagement.',
  [ContentStrategyType.ThoughtLeadership]: 'Builds credibility and attracts high-intent prospects.',
  [ContentStrategyType.LifestyleContent]: 'Helps customers picture themselves using your product.',
  [ContentStrategyType.ProblemSolution]: 'Connects directly with people actively experiencing that problem.',
  [ContentStrategyType.ProductEducation]: 'Turns curious browsers into informed buyers.',
  [ContentStrategyType.OfferAndServices]: 'Creates urgency and rewards customers for purchasing.',
  [ContentStrategyType.OfferAndPromotion]: 'Creates urgency and rewards customers for purchasing.',
}

export const description = {
  [ContentStrategyType.EducationalContent]:
    'Teach something useful about the broader category or problem space, not just your products.',
  [ContentStrategyType.QuickTips]: 'Offer focused, immediately applicable advice your audience can act on today.',
  [ContentStrategyType.ThoughtLeadership]:
    'Share original perspectives, industry insights, and expert takes on trends shaping your field.',
  [ContentStrategyType.LifestyleContent]:
    "Show your products in the context of your customer's aspirations and daily life.",
  [ContentStrategyType.ProblemSolution]: 'Present a common pain point your audience faces, then show how you solve it.',
  [ContentStrategyType.ProductEducation]:
    'Break down what makes your product/service great: the features, materials, design choices, and craft behind it.',
  [ContentStrategyType.OfferAndServices]: 'Highlight sales, discounts, bundles, or limited-time deals.',
  [ContentStrategyType.OfferAndPromotion]: 'Highlight sales, discounts, bundles, or limited-time deals.',
}
